/**
 * exportFile ツールで生成した Blob を、toolCallId をキーにセッション中だけ保持するストア。
 * 履歴に残せるのはツール結果テキストのみで Blob は残せないため、生成後はダウンロードカードが
 * 唯一の取得経路になる。履歴復元（リロード）後はストアが空になり、カードは「再生成を依頼」に切り替わる。
 */

type StoredExportedFile = { blob: Blob; filename: string };
type StoreEntry = StoredExportedFile & { downloaded: boolean };

// ダウンロード「済み」のファイルの保持上限。カードからの再ダウンロード用途なので、
// 通常利用で困らない範囲で低めに抑える。
const MAX_STORED_DOWNLOADED_FILES = 20;
// 未ダウンロードのファイルはカードからの唯一の取得経路であり、上限で失うと二度と取得できなくなるため、
// ダウンロード済みより大幅に高い上限にする（通常の利用では到達しない想定）。
// それでも無制限にはしない（クリックせず生成し続けるとBlobがメモリに際限なく積み上がるため）。
const MAX_STORED_UNDOWNLOADED_FILES = 200;

const store = new Map<string, StoreEntry>();

// ExportFileCard は React の useSyncExternalStore でこのストアを購読する。他のカードの
// ダウンロードによる evict でも、影響を受けたカードが自動で失効表示に切り替わるようにするため
// （購読しないと、evict後もボタン表示のまま残り、クリックしても無反応になってしまう）。
const listeners = new Set<() => void>();

export function subscribeToExportedChatFileStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

export function storeExportedChatFile(toolCallId: string, file: StoredExportedFile): void {
  store.set(toolCallId, { ...file, downloaded: false });
  evictOldUndownloadedEntriesOverLimit();
  notifyListeners();
}

export function getExportedChatFile(toolCallId: string): StoredExportedFile | undefined {
  return store.get(toolCallId);
}

// Blob をブラウザでダウンロードさせる（<a download> をクリック）。ダウンロード済みとして記録し、
// ダウンロード済みエントリが保持件数の上限を超えていれば古いものから破棄する。
export function downloadExportedChatFile(toolCallId: string, file: StoredExportedFile): void {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // click 直後に revoke するとブラウザによってはダウンロードが失敗するため、少し遅らせて解放する。
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);

  const entry = store.get(toolCallId);
  if (entry) entry.downloaded = true;
  evictOldDownloadedEntriesOverLimit();
  notifyListeners();
}

// Map は挿入順を保つので、ダウンロード済みエントリのうち最も古いものから破棄する。
function evictOldDownloadedEntriesOverLimit(): void {
  evictOldEntriesOverLimit((entry) => entry.downloaded, MAX_STORED_DOWNLOADED_FILES);
}

// 未ダウンロードのエントリも、通常利用では到達しない上限までは保持し、それを超えたら古いものから破棄する。
function evictOldUndownloadedEntriesOverLimit(): void {
  evictOldEntriesOverLimit((entry) => !entry.downloaded, MAX_STORED_UNDOWNLOADED_FILES);
}

function evictOldEntriesOverLimit(matches: (entry: StoreEntry) => boolean, limit: number): void {
  let count = 0;
  for (const entry of store.values()) {
    if (matches(entry)) count++;
  }
  while (count > limit) {
    const oldestKey = [...store.entries()].find(([, entry]) => matches(entry))?.[0];
    if (oldestKey === undefined) break;
    store.delete(oldestKey);
    count--;
  }
}
